/* eslint-disable @applitools/compat/no-async-await */
import { boot } from '@dazn/bootstrap-lib';

import {
    NATIVE_APP_IDENTIFIER,
    PARTNER_APP_IDENTIFIER,
    TEMP_AUTH_TOKEN,
    TEMP_NATIVE_TOKEN,
    validNativeApps,
} from '../configuration/constants';

import { getDeviceId } from './deviceId';
import { getGuid } from './guid';
import { parseUrlParameters } from './parseUrlParameters';

export type PlatformAttributes = Record<string, unknown>;

interface StorageProps {
    /**
     * Function for retrieving locally saved data (usually wrapper arround `localStorage.getItem`). Library expects this function to return `Promise`
     */
    load: boot.storage.AsyncStorage['getItem'];
    /**
     * Function for saving data locally (usually wrapper arround `localStorage.setItem`). Library expects this function to return `Promise`
     */
    save: boot.storage.AsyncStorage['setItem'];
}

interface DeviceDetails {
    /**
     * Know as well as target
     */
    platform: string;
    manufacturer: string;
}

interface ConfigProps {
    /**
     * Version of bootstrap
     */
    bootstrapVersion: string;
    /**
     * Version of target (not this library)
     * aka platform: target-web or tv-targets
     */
    targetVersion: string;
    /**
     * Environment for which the bootstrap is built
     */
    environment: boot.config.Environment;
    environmentGroup: string;
    /**
     * Optional configuration that let's you choose what sort of indicator to display when device is in dev mode: `circle` or `line`. Defaults to: `line`
     */
    sessionId: string;
}

export interface CreateInitializationModuleProps {
    asyncStorage: StorageProps;
    deviceDetails: DeviceDetails;
    config: ConfigProps;
    getCurrentUrl: () => string;
    fetchDeviceId?: () => Promise<string>;
}

const NOT_A_CHAPTER_NAME = ['bootstrap', 'target'];

/**
 * Handles initial data such as deviceId, platform etc
 */
export const createInitializationModule = ({
    asyncStorage: { load, save },
    deviceDetails: { platform, manufacturer },
    config: {
        bootstrapVersion,
        targetVersion,
        environment,
        environmentGroup,
        sessionId,
    },
    getCurrentUrl,
    fetchDeviceId,
}: CreateInitializationModuleProps) => {
    let deviceId: string;
    let guid: string;
    const versions: boot.config.ChaptersVersions = {
        bootstrap: bootstrapVersion,
        target: targetVersion,
    };

    const initialUrl = getCurrentUrl();
    const initialUrlParameters = parseUrlParameters(initialUrl);

    window.newrelic?.setCustomAttribute('platform', platform);
    window.newrelic?.setCustomAttribute('manufacturer', manufacturer);
    window.newrelic?.setCustomAttribute('environmentGroup', environmentGroup);

    const getNativeTempToken = async (endPointUrl: string) => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const nativeValue = urlParams.get(TEMP_NATIVE_TOKEN);

            if (!nativeValue) {
                return;
            }

            const res = await fetch(endPointUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${nativeValue}`,
                },
            });

            if (!res.ok) throw new Error('Failed to fetch data');

            const data = await res.json();

            return data.access_token;
        } catch (err) {
            throw err;
        }
    };

    const getTempAuthToken = async (endPointUrl: string, brand: string) => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const nativeValue = urlParams.get(TEMP_AUTH_TOKEN);

            if (!nativeValue) {
                return;
            }

            const res = await fetch(endPointUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${nativeValue}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    deviceId: deviceId,
                    brand,
                }),
            });

            if (!res.ok) throw new Error('Failed to fetch data');

            const data = await res.json();
            return data.AuthToken?.Token;
        } catch (err) {
            throw err;
        }
    };

    const checkNativeAppStatus = (): {
        isNative: boolean;
        nativeApp: string;
    } => {
        const urlParams = new URLSearchParams(window.location?.search);
        const nativeValue = urlParams.get(NATIVE_APP_IDENTIFIER)?.toLowerCase();

        const storedNativeApp = window.localStorage.getItem(
            NATIVE_APP_IDENTIFIER,
        );

        const hasValidNativeFlagInStorage = (): boolean =>
            !!(
                storedNativeApp &&
                validNativeApps.includes(storedNativeApp.toLowerCase())
            );

        if (nativeValue && validNativeApps.includes(nativeValue)) {
            window.localStorage.setItem(NATIVE_APP_IDENTIFIER, nativeValue);
            window.newrelic?.setCustomAttribute('nativeApp', nativeValue);
            return { isNative: true, nativeApp: nativeValue };
        }

        if (
            hasValidNativeFlagInStorage() &&
            nativeValue !== undefined &&
            !validNativeApps.includes(nativeValue)
        ) {
            localStorage.removeItem(NATIVE_APP_IDENTIFIER);
            return {
                isNative: false,
                nativeApp: '',
            };
        }

        if (hasValidNativeFlagInStorage()) {
            window.newrelic?.setCustomAttribute('nativeApp', storedNativeApp!);
            return {
                isNative: true,
                nativeApp: storedNativeApp || '',
            };
        }

        return { isNative: false, nativeApp: '' };
    };

    const checkPartnerApp = (): { partnerApp: string } => {
        const urlParams = new URLSearchParams(window.location?.search);
        const partnerValue = urlParams
            .get(PARTNER_APP_IDENTIFIER)
            ?.toLowerCase();

        return {
            partnerApp: partnerValue || '',
        };
    };

    const result = {
        getEnvironment: () => environment,
        getEnvironmentGroup: () => environmentGroup,
        getVersions: () => versions,
        setChapterVersion: (
            chapterName: boot.config.ChapterName,
            version: string | null,
        ) => {
            if (NOT_A_CHAPTER_NAME.some(nacn => nacn === chapterName)) {
                return false;
            }
            if (typeof version !== 'string') {
                return false;
            }

            versions[chapterName] = version.replace(/_/g, '.');
            window.newrelic?.setCustomAttribute(
                'chapterVersion',
                String(versions[chapterName]),
            );

            return true;
        },
        getPlatform: () => platform,
        getManufacturer: () => manufacturer,
        getSessionId: () => sessionId,
        setSessionId: (value: string) => {
            sessionId = value;
        },
        getDeviceId: () => deviceId,
        getGuid: () => guid,
        getInitialUrl: () => initialUrl,
        getInitialUrlParameters: () => initialUrlParameters,
        run: () => {
            getGuid(load, save).then(value => {
                guid = value;
            });

            const deviceIdPromise = fetchDeviceId
                ? fetchDeviceId()
                : getDeviceId(load, save);
            return deviceIdPromise.then(value => {
                window.newrelic?.setCustomAttribute('deviceId', value);
                deviceId = value;
            });
        },
        checkNativeAppStatus,
        getNativeTempToken,
        getTempAuthToken,
        checkPartnerApp,
    };

    return result;
};

export type InitializationModule = ReturnType<
    typeof createInitializationModule
>;
